import React, { useState, useEffect, useCallback } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { authAPI, subscriptionAPI } from '../../services/api';
import { useAuth } from '../../contexts/AuthContext';
import { toast } from 'react-hot-toast';
import { useGoogleLogin } from '@react-oauth/google';
import { PublicClientApplication } from '@azure/msal-browser';
import useDocumentTitle from '../../hooks/useDocumentTitle';
import { countries } from '../../data/countries';
import './AuthPages.css';

interface StepContent {
  title: string;
  description: string;
  features: string[];
}

interface Plan {
  id: number;
  name: string;
  slug: string;
  description: string;
  price_monthly: number;
  price_yearly: number;
  features: Record<string, boolean>;
  limits: Record<string, number>;
  trial_days: number;
  sort_order: number;
}

// Plan display helpers
const planColors: Record<string, string> = {
  free: '#6c757d',
  starter: '#0d6efd',
  professional: '#6f42c1',
  enterprise: '#dc3545',
};

const planIcons: Record<string, string> = {
  free: 'bi-gift',
  starter: 'bi-rocket-takeoff',
  professional: 'bi-star-fill',
  enterprise: 'bi-building',
};

const featureLabels: { key: string; label: string }[] = [
  { key: 'analytics', label: 'Analytics Dashboard' },
  { key: 'monitoring', label: 'Website Monitoring' },
  { key: 'security', label: 'Cybersecurity Auditing' },
  { key: 'server_monitoring', label: 'Server Monitoring' },
  { key: 'ai_advisor', label: 'Intelligent Security Monitoring' },
  { key: 'reports', label: 'Reports & Exports' },
  { key: 'alerts', label: 'Alerts & Notifications' },
  { key: 'event_points', label: 'Register Events & Conversion Tracking' },
  { key: 'user_permissions', label: 'User Permissions & Roles' },
  { key: 'seo', label: 'SEO Scanner & Summary' },
  { key: 'seo_ai', label: 'AI SEO Optimizer' },
  { key: 'api_access', label: 'API Access' },
  { key: 'priority_support', label: 'Priority Support' },
];

interface LoginMethods {
  otp: boolean;
  google: boolean;
  microsoft: boolean;
  google_client_id: string;
  microsoft_client_id: string;
  microsoft_tenant_id: string;
}

const ModernSignupPage: React.FC = () => {
  useDocumentTitle('Sign Up');
  const [currentStep, setCurrentStep] = useState(1);
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();
  const { login } = useAuth();
  const [searchParams] = useSearchParams();

  // Step 1: Email Verification
  const [email, setEmail] = useState('');
  const [otpSent, setOtpSent] = useState(false);
  const [otp, setOtp] = useState('');

  // Step 2: Personal Details
  const [name, setName] = useState('');
  const [countryCode, setCountryCode] = useState('+973'); // Bahrain default
  const [mobile, setMobile] = useState('');

  // Step 3: Organization Setup
  const [orgName, setOrgName] = useState('');
  const [orgType, setOrgType] = useState('');
  const [orgIndustry, setOrgIndustry] = useState('');
  const [orgCountry, setOrgCountry] = useState('');
  const [orgCurrency, setOrgCurrency] = useState('USD');

  // Step 4: Plan Selection
  const [plans, setPlans] = useState<Plan[]>([]);
  const [selectedPlanId, setSelectedPlanId] = useState<number>(1); // default Free
  const [billingPeriod, setBillingPeriod] = useState<'monthly' | 'yearly'>('monthly');
  const [plansLoading, setPlansLoading] = useState(false);
  const [currency, setCurrency] = useState('USD');

  // OAuth
  const [loginMethods, setLoginMethods] = useState<LoginMethods | null>(null);
  const [oauthLoading, setOauthLoading] = useState<string | null>(null);

  // Supported billing currencies (must match backend SUPPORTED_CURRENCIES)
  const BILLING_CURRENCIES = ['USD', 'AED', 'BHD', 'EUR', 'GBP', 'KWD', 'OMR', 'QAR', 'SAR'];
  // Exchange rates from USD (client-side conversion for plan preview)
  const BILLING_RATES: Record<string, number> = {
    USD: 1.0, AED: 3.6725, BHD: 0.376, EUR: 0.92, GBP: 0.79,
    KWD: 0.307, OMR: 0.385, QAR: 3.64, SAR: 3.75,
  };
  const BILLING_DECIMALS: Record<string, number> = { KWD: 3, BHD: 3, OMR: 3 };

  // Auto-derive billing currency from selected org country
  useEffect(() => {
    if (orgCountry) {
      const c = countries.find(co => co.code === orgCountry);
      const derived = c && BILLING_CURRENCIES.includes(c.currency) ? c.currency : 'USD';
      setOrgCurrency(derived);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [orgCountry]);

  // Fetch login methods on mount
  useEffect(() => {
    authAPI.getLoginMethods()
      .then((res) => setLoginMethods(res.data))
      .catch(() => setLoginMethods({ otp: true, google: false, microsoft: false, google_client_id: '', microsoft_client_id: '', microsoft_tenant_id: '' }));
  }, []);

  // Check for OAuth-verified session (redirected from login page OAuth)
  useEffect(() => {
    const oauthVerified = sessionStorage.getItem('oauth_verified');
    const oauthEmail = sessionStorage.getItem('oauth_email');
    const oauthName = sessionStorage.getItem('oauth_name');

    if (oauthVerified && oauthEmail) {
      setEmail(oauthEmail);
      setName(oauthName || '');
      setCurrentStep(2); // Skip step 1 (email verification)
      // Clean up
      sessionStorage.removeItem('oauth_verified');
      sessionStorage.removeItem('oauth_email');
      sessionStorage.removeItem('oauth_name');
    }
  }, []);

  // Pre-fill email from URL params (e.g. redirected from login page)
  useEffect(() => {
    const prefillEmail = searchParams.get('email');
    if (prefillEmail && !sessionStorage.getItem('oauth_verified')) {
      setEmail(prefillEmail);
    }
  }, [searchParams]);

  // Handle OAuth response for signup
  const handleOAuthSignup = useCallback(async (data: any) => {
    if (data.action === 'login') {
      // User already exists, log them in
      login(data.user, data.token);
      toast.success('Login successful! You already have an account.');
      navigate('/analytics/summary');
    } else if (data.action === 'register') {
      // New user — skip step 1 and go to step 2
      setEmail(data.email);
      setName(data.name || '');
      setCurrentStep(2);
      toast.success('Email verified! Please complete your registration.');
    }
  }, [login, navigate]);

  // Google signup handler
  const googleSignup = useGoogleLogin({
    onSuccess: async (tokenResponse) => {
      setOauthLoading('google');
      try {
        const res = await authAPI.googleOAuth(tokenResponse.access_token);
        if (res.data.status === 'success') {
          await handleOAuthSignup(res.data);
        } else {
          toast.error(res.data.message || 'Google signup failed');
        }
      } catch (error: any) {
        toast.error(error.response?.data?.message || 'Google signup failed');
      } finally {
        setOauthLoading(null);
      }
    },
    onError: () => {
      toast.error('Google login was cancelled');
      setOauthLoading(null);
    },
    flow: 'implicit',
  });

  // Microsoft signup handler
  const handleMicrosoftSignup = async () => {
    if (!loginMethods?.microsoft_client_id) return;
    setOauthLoading('microsoft');
    try {
      const msalInstance = new PublicClientApplication({
        auth: {
          clientId: loginMethods.microsoft_client_id,
          authority: `https://login.microsoftonline.com/${loginMethods.microsoft_tenant_id || 'common'}`,
          redirectUri: window.location.origin,
        },
      });
      await msalInstance.initialize();
      const result = await msalInstance.loginPopup({
        scopes: ['User.Read'],
      });

      const res = await authAPI.microsoftOAuth(result.accessToken);
      if (res.data.status === 'success') {
        await handleOAuthSignup(res.data);
      } else {
        toast.error(res.data.message || 'Microsoft signup failed');
      }
    } catch (error: any) {
      if (error.errorCode !== 'user_cancelled') {
        toast.error(error.response?.data?.message || 'Microsoft signup failed');
      }
    } finally {
      setOauthLoading(null);
    }
  };

  const stepContent: Record<number, StepContent> = {
    1: {
      title: 'Verify Your Email',
      description: 'We use email verification instead of passwords for enhanced security. Check your inbox for the verification code.',
      features: [
        'Passwordless authentication',
        'Enhanced security',
        'Quick & easy setup'
      ]
    },
    2: {
      title: 'Tell Us About You',
      description: 'Help us personalize your experience by providing some basic information about yourself.',
      features: [
        'Personalized dashboard',
        'Custom notifications',
        'Team collaboration ready'
      ]
    },
    3: {
      title: 'Setup Your Organization',
      description: 'Create your organization profile to start managing your digital assets effectively.',
      features: [
        'Multi-asset management',
        'Team member invites',
        'Centralized control'
      ]
    },
    4: {
      title: 'Choose Your Plan',
      description: 'Select the plan that best fits your needs. Start with a free trial or subscribe directly.',
      features: [
        'Free trial available on paid plans',
        'No credit card required for trial',
        'Upgrade anytime'
      ]
    }
  };

  const fetchPlans = async () => {
    setPlansLoading(true);
    try {
      const response = await subscriptionAPI.listPlans();
      const plansData = response.data?.data;
      const plansArray = Array.isArray(plansData) ? plansData : (plansData?.plans || []);
      const detectedCurrency = Array.isArray(plansData) ? 'USD' : (plansData?.currency || 'USD');
      setPlans(plansArray);
      setCurrency(detectedCurrency);
    } catch {
      toast.error('Failed to load plans');
    } finally {
      setPlansLoading(false);
    }
  };

  const handleSendOTP = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);

    try {
      const response = await authAPI.sendSignupOTP(email);

      if (response.data.status === 'error') {
        toast.error(response.data.message);

        // If user already exists, redirect to login
        if (response.data.redirect_to_login) {
          setTimeout(() => navigate('/login'), 2000);
        }
        return;
      }

      setOtpSent(true);
      toast.success('OTP sent to your email!');
    } catch (error: any) {
      toast.error(error.response?.data?.message || 'Failed to send OTP');
    } finally {
      setLoading(false);
    }
  };

  const handleVerifyOTP = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);

    try {
      const response = await authAPI.verifySignupOTP(email, otp);

      if (response.data.status === 'error') {
        toast.error(response.data.message);
        return;
      }

      toast.success('Email verified successfully!');
      setCurrentStep(2);
    } catch (error: any) {
      toast.error(error.response?.data?.message || 'Invalid OTP');
    } finally {
      setLoading(false);
    }
  };

  const handlePersonalDetails = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!name.trim()) {
      toast.error('Please enter your name');
      return;
    }

    // Pre-populate org country from selected phone code if not already set
    if (!orgCountry) {
      const found = countries.find(c => c.phoneCode === countryCode);
      if (found) setOrgCountry(found.code);
    }

    setCurrentStep(3);
    toast.success('Personal details saved!');
  };

  const handleOrganizationSetup = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!orgName.trim() || !orgType || !orgIndustry || !orgCountry) {
      toast.error('Please fill in all organization details');
      return;
    }

    // Fetch plans and move to step 4
    await fetchPlans();
    setCurrentStep(4);
  };

  // Start Free Trial (existing flow)
  const handleStartTrial = async () => {
    setLoading(true);

    try {
      const response = await authAPI.completeRegistration({
        email,
        name,
        country_code: countryCode,
        mobile,
        organization_name: orgName,
        organization_type: orgType,
        industry: orgIndustry,
        country: orgCountry || undefined,
        currency: orgCurrency || undefined,
        plan_id: selectedPlanId,
      });

      if (response.data.status === 'error') {
        toast.error(response.data.message);
        if (response.data.errors) {
          Object.values(response.data.errors).forEach((error: any) => {
            toast.error(error);
          });
        }
        return;
      }

      const { token, user } = response.data;
      login(user, token);

      const selectedPlan = plans.find(p => p.id === selectedPlanId);
      if (selectedPlan && selectedPlan.trial_days > 0) {
        toast.success(`Welcome! Your ${selectedPlan.trial_days}-day free trial of ${selectedPlan.name} has started.`);
      } else {
        toast.success('Welcome to CloudAyn! Let\'s add your first asset.');
      }
      navigate('/setup/asset');
    } catch (error: any) {
      toast.error(error.response?.data?.message || 'Registration failed');
    } finally {
      setLoading(false);
    }
  };

  // Subscribe Now - register on free plan, then redirect to checkout
  const handleSubscribeNow = async () => {
    setLoading(true);

    try {
      const response = await authAPI.completeRegistration({
        email,
        name,
        country_code: countryCode,
        mobile,
        organization_name: orgName,
        organization_type: orgType,
        industry: orgIndustry,
        country: orgCountry || undefined,
        currency: orgCurrency || undefined,
        plan_id: selectedPlanId,
        skip_trial: true,
      });

      if (response.data.status === 'error') {
        toast.error(response.data.message);
        if (response.data.errors) {
          Object.values(response.data.errors).forEach((error: any) => {
            toast.error(error);
          });
        }
        return;
      }

      const { token, user } = response.data;

      // Store redirect BEFORE login, so PublicRoute uses it instead of default redirect
      sessionStorage.setItem('post_login_redirect', `/billing/checkout?plan=${selectedPlanId}&period=${billingPeriod}`);
      // Set payment-pending flags to force checkout before dashboard access
      sessionStorage.setItem('payment_pending', 'true');
      sessionStorage.setItem('payment_pending_plan', String(selectedPlanId));
      sessionStorage.setItem('payment_pending_period', billingPeriod);
      login(user, token);

      toast.success('Account created! Redirecting to checkout...');
    } catch (error: any) {
      toast.error(error.response?.data?.message || 'Registration failed');
    } finally {
      setLoading(false);
    }
  };

  // Get Started Free (free plan)
  const handleGetStartedFree = async () => {
    setLoading(true);

    try {
      const response = await authAPI.completeRegistration({
        email,
        name,
        country_code: countryCode,
        mobile,
        organization_name: orgName,
        organization_type: orgType,
        industry: orgIndustry,
        country: orgCountry || undefined,
        currency: orgCurrency || undefined,
        plan_id: selectedPlanId,
      });

      if (response.data.status === 'error') {
        toast.error(response.data.message);
        return;
      }

      const { token, user } = response.data;
      login(user, token);

      toast.success('Welcome to CloudAyn! Let\'s add your first asset.');
      navigate('/setup/asset');
    } catch (error: any) {
      toast.error(error.response?.data?.message || 'Registration failed');
    } finally {
      setLoading(false);
    }
  };

  const decimals = BILLING_DECIMALS[orgCurrency] ?? 2;

  const formatPrice = (plan: Plan) => {
    const usdPrice = Number(billingPeriod === 'yearly' ? plan.price_yearly : plan.price_monthly);
    if (usdPrice <= 0) return 'Free';
    const rate = BILLING_RATES[orgCurrency] || 1.0;
    const converted = parseFloat((usdPrice * rate).toFixed(decimals));
    return `${orgCurrency} ${converted.toFixed(decimals)}`;
  };

  const formatLimitValue = (value: number) => {
    if (value === -1) return 'Unlimited';
    return value.toString();
  };

  const renderStepContent = () => {
    switch (currentStep) {
      case 1:
        return !otpSent ? (
          <div className="auth-form step-content">
            <form onSubmit={handleSendOTP}>
              <div className="form-group">
                <label>Email Address</label>
                <div className="input-with-icon">
                  <i className="bi bi-envelope"></i>
                  <input
                    type="email"
                    className="form-control"
                    placeholder="you@company.com"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    required
                  />
                </div>
              </div>

              <button type="submit" className="btn btn-primary btn-lg w-100" disabled={loading}>
                {loading ? (
                  <>
                    <span className="spinner-border spinner-border-sm me-2"></span>
                    Sending OTP...
                  </>
                ) : (
                  <>
                    Continue with Email
                    <i className="bi bi-arrow-right ms-2"></i>
                  </>
                )}
              </button>
            </form>

            {/* OAuth Buttons */}
            {loginMethods && (loginMethods.google || loginMethods.microsoft) && (
              <>
                <div className="auth-divider" style={{ margin: '1rem 0' }}>
                  <span>or sign up with</span>
                </div>
                <div className="oauth-buttons">
                  {loginMethods.google && (
                    <button
                      type="button"
                      className="btn btn-outline-secondary btn-lg w-100 d-flex align-items-center justify-content-center gap-2"
                      onClick={() => googleSignup()}
                      disabled={oauthLoading !== null}
                    >
                      {oauthLoading === 'google' ? (
                        <span className="spinner-border spinner-border-sm"></span>
                      ) : (
                        <svg width="20" height="20" viewBox="0 0 24 24">
                          <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92a5.06 5.06 0 0 1-2.2 3.32v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.1z"/>
                          <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
                          <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
                          <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
                        </svg>
                      )}
                      Google
                    </button>
                  )}
                  {loginMethods.microsoft && (
                    <button
                      type="button"
                      className="btn btn-outline-secondary btn-lg w-100 d-flex align-items-center justify-content-center gap-2"
                      onClick={handleMicrosoftSignup}
                      disabled={oauthLoading !== null}
                    >
                      {oauthLoading === 'microsoft' ? (
                        <span className="spinner-border spinner-border-sm"></span>
                      ) : (
                        <svg width="20" height="20" viewBox="0 0 23 23">
                          <rect fill="#f35325" x="1" y="1" width="10" height="10"/>
                          <rect fill="#81bc06" x="12" y="1" width="10" height="10"/>
                          <rect fill="#05a6f0" x="1" y="12" width="10" height="10"/>
                          <rect fill="#ffba08" x="12" y="12" width="10" height="10"/>
                        </svg>
                      )}
                      Microsoft
                    </button>
                  )}
                </div>
              </>
            )}
          </div>
        ) : (
          <form onSubmit={handleVerifyOTP} className="auth-form step-content">
            <div className="otp-sent-message">
              <i className="bi bi-check-circle-fill"></i>
              <p>We've sent a verification code to <strong>{email}</strong></p>
            </div>

            <div className="form-group">
              <label>Verification Code</label>
              <div className="input-with-icon">
                <i className="bi bi-shield-lock"></i>
                <input
                  type="text"
                  className="form-control"
                  placeholder="Enter 6-digit code"
                  value={otp}
                  onChange={(e) => setOtp(e.target.value)}
                  maxLength={6}
                  required
                />
              </div>
            </div>

            <button type="submit" className="btn btn-primary btn-lg w-100" disabled={loading}>
              {loading ? (
                <>
                  <span className="spinner-border spinner-border-sm me-2"></span>
                  Verifying...
                </>
              ) : (
                <>
                  Verify & Continue
                  <i className="bi bi-arrow-right ms-2"></i>
                </>
              )}
            </button>

            <button
              type="button"
              className="btn btn-link w-100"
              onClick={() => setOtpSent(false)}
            >
              Change email address
            </button>
          </form>
        );

      case 2:
        return (
          <form onSubmit={handlePersonalDetails} className="auth-form step-content">
            <div className="form-group">
              <label>Full Name</label>
              <div className="input-with-icon">
                <i className="bi bi-person"></i>
                <input
                  type="text"
                  className="form-control"
                  placeholder="John Doe"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  required
                />
              </div>
            </div>

            <div className="form-group">
              <label>Phone Number (Optional)</label>
              <div className="row g-2">
                <div className="col-4">
                  <select
                    className="form-control"
                    value={countryCode}
                    onChange={(e) => setCountryCode(e.target.value)}
                  >
                    {/* GCC Countries */}
                    <optgroup label="GCC Countries">
                      <option value="+973">{countries.find(c => c.code === 'BH')?.flag} +973 Bahrain</option>
                      <option value="+971">{countries.find(c => c.code === 'AE')?.flag} +971 UAE</option>
                      <option value="+966">{countries.find(c => c.code === 'SA')?.flag} +966 Saudi Arabia</option>
                      <option value="+965">{countries.find(c => c.code === 'KW')?.flag} +965 Kuwait</option>
                      <option value="+968">{countries.find(c => c.code === 'OM')?.flag} +968 Oman</option>
                      <option value="+974">{countries.find(c => c.code === 'QA')?.flag} +974 Qatar</option>
                    </optgroup>
                    {/* All Countries */}
                    <optgroup label="All Countries">
                      {countries.map(country => (
                        <option key={country.code} value={country.phoneCode}>
                          {country.flag} {country.phoneCode} {country.name}
                        </option>
                      ))}
                    </optgroup>
                  </select>
                </div>
                <div className="col-8">
                  <div className="input-with-icon">
                    <i className="bi bi-telephone"></i>
                    <input
                      type="tel"
                      className="form-control"
                      placeholder="Mobile number"
                      value={mobile}
                      onChange={(e) => setMobile(e.target.value.replace(/\D/g, ''))}
                      pattern="[0-9]*"
                    />
                  </div>
                </div>
              </div>
            </div>

            <button type="submit" className="btn btn-primary btn-lg w-100">
              Continue
              <i className="bi bi-arrow-right ms-2"></i>
            </button>

            <button
              type="button"
              className="btn btn-link w-100"
              onClick={() => setCurrentStep(1)}
            >
              <i className="bi bi-arrow-left me-2"></i>
              Back
            </button>
          </form>
        );

      case 3:
        return (
          <form onSubmit={handleOrganizationSetup} className="auth-form step-content">
            <div className="form-group">
              <label>Organization Name</label>
              <div className="input-with-icon">
                <i className="bi bi-building"></i>
                <input
                  type="text"
                  className="form-control"
                  placeholder="Acme Corporation"
                  value={orgName}
                  onChange={(e) => setOrgName(e.target.value)}
                  required
                />
              </div>
            </div>

            <div className="form-group">
              <label>Organization Type</label>
              <div className="input-with-icon">
                <i className="bi bi-briefcase"></i>
                <select
                  className="form-control"
                  value={orgType}
                  onChange={(e) => setOrgType(e.target.value)}
                  required
                >
                  <option value="">Select type...</option>
                  <option value="startup">Startup</option>
                  <option value="small_business">Small Business</option>
                  <option value="medium_business">Medium Business</option>
                  <option value="enterprise">Enterprise</option>
                  <option value="agency">Agency</option>
                  <option value="freelancer">Freelancer</option>
                  <option value="non_profit">Non-Profit</option>
                  <option value="education">Education</option>
                </select>
              </div>
            </div>

            <div className="form-group">
              <label>Industry</label>
              <div className="input-with-icon">
                <i className="bi bi-gear"></i>
                <select
                  className="form-control"
                  value={orgIndustry}
                  onChange={(e) => setOrgIndustry(e.target.value)}
                  required
                >
                  <option value="">Select industry...</option>
                  <option value="technology">Technology</option>
                  <option value="ecommerce">E-commerce</option>
                  <option value="healthcare">Healthcare</option>
                  <option value="finance">Finance</option>
                  <option value="education">Education</option>
                  <option value="marketing">Marketing</option>
                  <option value="media">Media & Entertainment</option>
                  <option value="real_estate">Real Estate</option>
                  <option value="manufacturing">Manufacturing</option>
                  <option value="retail">Retail</option>
                  <option value="hospitality">Hospitality</option>
                  <option value="other">Other</option>
                </select>
              </div>
            </div>

            <div className="form-group">
              <label>Country</label>
              <div className="input-with-icon">
                <i className="bi bi-globe2"></i>
                <select
                  className="form-control"
                  value={orgCountry}
                  onChange={(e) => setOrgCountry(e.target.value)}
                  required
                >
                  <option value="">Select country…</option>
                  <optgroup label="GCC Countries">
                    {['BH', 'AE', 'SA', 'KW', 'OM', 'QA'].map(code => {
                      const c = countries.find(co => co.code === code);
                      return c ? <option key={code} value={code}>{c.flag} {c.name}</option> : null;
                    })}
                  </optgroup>
                  <optgroup label="All Countries">
                    {countries.map(c => (
                      <option key={c.code} value={c.code}>{c.flag} {c.name}</option>
                    ))}
                  </optgroup>
                </select>
              </div>
              {orgCountry && (
                <div className="mt-1" style={{ fontSize: '12px', color: '#6b7280' }}>
                  <i className="bi bi-currency-exchange me-1"></i>
                  Billing currency: <strong>{orgCurrency}</strong>
                  {orgCurrency !== 'USD' && <span className="ms-1 text-muted">(plans priced in {orgCurrency})</span>}
                </div>
              )}
            </div>

            <button type="submit" className="btn btn-primary btn-lg w-100" disabled={loading}>
              Continue
              <i className="bi bi-arrow-right ms-2"></i>
            </button>

            <button
              type="button"
              className="btn btn-link w-100"
              onClick={() => setCurrentStep(2)}
              disabled={loading}
            >
              <i className="bi bi-arrow-left me-2"></i>
              Back
            </button>
          </form>
        );

      case 4:
        return (
          <div className="auth-form step-content">
            {plansLoading ? (
              <div className="text-center py-4">
                <span className="spinner-border spinner-border-sm me-2"></span>
                Loading plans...
              </div>
            ) : (
              <>
                {/* Billing toggle */}
                <div className="d-flex justify-content-center mb-3">
                  <div className="btn-group" role="group">
                    <button
                      type="button"
                      className={`btn btn-sm ${billingPeriod === 'monthly' ? 'btn-primary' : 'btn-outline-secondary'}`}
                      onClick={() => setBillingPeriod('monthly')}
                    >
                      Monthly
                    </button>
                    <button
                      type="button"
                      className={`btn btn-sm ${billingPeriod === 'yearly' ? 'btn-primary' : 'btn-outline-secondary'}`}
                      onClick={() => setBillingPeriod('yearly')}
                    >
                      Yearly <span className="badge bg-success ms-1" style={{ fontSize: '0.65rem' }}>Save 20%</span>
                    </button>
                  </div>
                </div>

                {/* Plan cards */}
                <div className="plan-cards-signup">
                  {plans.map((plan, index) => {
                    const isSelected = selectedPlanId === plan.id;
                    const isFree = plan.slug === 'free';
                    const price = billingPeriod === 'yearly' ? plan.price_yearly : plan.price_monthly;
                    const slug = plan.slug || 'free';
                    const color = planColors[slug] || '#0d6efd';
                    const icon = planIcons[slug] || 'bi-box';
                    const features = typeof plan.features === 'string' ? JSON.parse(plan.features) : (plan.features || {});
                    const limits = typeof plan.limits === 'string' ? JSON.parse(plan.limits) : (plan.limits || {});
                    const prevPlanName = index > 0 ? plans[index - 1]?.name : null;
                    const enabledFeatures = featureLabels.filter(f => features[f.key]);

                    return (
                      <div
                        key={plan.id}
                        className={`plan-card-mini ${isSelected ? 'selected' : ''}`}
                        onClick={() => setSelectedPlanId(plan.id)}
                        style={{
                          border: isSelected ? `2px solid ${color}` : '1px solid #e5e7eb',
                          borderRadius: '10px',
                          padding: '12px',
                          marginBottom: '8px',
                          cursor: 'pointer',
                          backgroundColor: isSelected ? `${color}08` : '#fff',
                          transition: 'all 0.2s',
                        }}
                      >
                        <div className="d-flex justify-content-between align-items-start">
                          <div className="d-flex align-items-center gap-2">
                            <div className="d-flex align-items-center justify-content-center"
                              style={{ width: '32px', height: '32px', borderRadius: '50%', backgroundColor: `${color}15`, flexShrink: 0 }}>
                              <i className={`bi ${icon}`} style={{ fontSize: '0.9rem', color }}></i>
                            </div>
                            <div>
                              <div className="d-flex align-items-center gap-2">
                                <strong style={{ fontSize: '0.95rem' }}>{plan.name}</strong>
                                {plan.trial_days > 0 && !isFree && (
                                  <span className="badge bg-info" style={{ fontSize: '0.6rem' }}>
                                    {plan.trial_days}-day trial
                                  </span>
                                )}
                              </div>
                              <div style={{ fontSize: '0.75rem', color: '#6b7280', marginTop: '1px' }}>
                                {limits?.assets === -1 ? 'Unlimited' : limits?.assets || 1} assets
                                {' / '}
                                {limits?.users === -1 ? 'Unlimited' : limits?.users || 1} users
                                {' / '}
                                {limits?.retention_days || 30} days retention
                              </div>
                            </div>
                          </div>
                          <div className="text-end" style={{ flexShrink: 0 }}>
                            <div style={{ fontWeight: 600, fontSize: '1rem', color }}>
                              {isFree ? 'Free' : `${currency} ${Number(price).toFixed(decimals)}`}
                            </div>
                            {!isFree && (
                              <div style={{ fontSize: '0.7rem', color: '#9ca3af' }}>
                                /{billingPeriod === 'yearly' ? 'year' : 'month'}
                              </div>
                            )}
                          </div>
                        </div>

                        {/* Feature inclusions */}
                        <div style={{ marginTop: '8px', paddingTop: '8px', borderTop: '1px solid #f3f4f6' }}>
                          {prevPlanName && (
                            <div style={{ fontSize: '0.72rem', color, fontWeight: 600, marginBottom: '4px' }}>
                              <i className="bi bi-plus-circle me-1"></i>
                              Everything in {prevPlanName}, plus:
                            </div>
                          )}
                          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '2px 8px' }}>
                            {enabledFeatures.map(f => (
                              <div key={f.key} style={{ fontSize: '0.72rem', color: '#374151' }}>
                                <i className="bi bi-check-lg me-1" style={{ color, fontWeight: 700 }}></i>
                                {f.label}
                              </div>
                            ))}
                            {enabledFeatures.length === 0 && (
                              <div style={{ fontSize: '0.72rem', color: '#9ca3af', gridColumn: '1 / -1' }}>
                                Basic features included
                              </div>
                            )}
                          </div>
                        </div>

                        {isSelected && (
                          <div style={{ marginTop: '8px', fontSize: '0.75rem', color }}>
                            <i className="bi bi-check-circle-fill me-1"></i>
                            {isFree
                              ? 'Start with basic features'
                              : (plan.trial_days > 0
                                  ? `${plan.trial_days}-day free trial available, or subscribe now`
                                  : 'Subscribe to get started'
                                )
                            }
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>

                {/* Action Buttons - conditional based on selected plan */}
                {(() => {
                  const selectedPlan = plans.find(p => p.id === selectedPlanId);
                  const isFree = selectedPlan?.slug === 'free';
                  const hasTrial = selectedPlan && selectedPlan.trial_days > 0;

                  if (isFree) {
                    return (
                      <button
                        type="button"
                        className="btn btn-primary btn-lg w-100 mt-3"
                        onClick={handleGetStartedFree}
                        disabled={loading}
                      >
                        {loading ? (
                          <>
                            <span className="spinner-border spinner-border-sm me-2"></span>
                            Creating Account...
                          </>
                        ) : (
                          <>
                            Get Started Free
                            <i className="bi bi-check-circle ms-2"></i>
                          </>
                        )}
                      </button>
                    );
                  }

                  return (
                    <div className="mt-3 d-flex flex-column gap-2">
                      {hasTrial && (
                        <button
                          type="button"
                          className="btn btn-outline-primary btn-lg w-100"
                          onClick={handleStartTrial}
                          disabled={loading}
                        >
                          {loading ? (
                            <>
                              <span className="spinner-border spinner-border-sm me-2"></span>
                              Creating Account...
                            </>
                          ) : (
                            <>
                              Start Free Trial ({selectedPlan.trial_days} days)
                              <i className="bi bi-clock ms-2"></i>
                            </>
                          )}
                        </button>
                      )}
                      <button
                        type="button"
                        className="btn btn-primary btn-lg w-100"
                        onClick={handleSubscribeNow}
                        disabled={loading}
                      >
                        {loading ? (
                          <>
                            <span className="spinner-border spinner-border-sm me-2"></span>
                            Creating Account...
                          </>
                        ) : (
                          <>
                            Subscribe Now
                            <i className="bi bi-credit-card ms-2"></i>
                          </>
                        )}
                      </button>
                    </div>
                  );
                })()}

                <button
                  type="button"
                  className="btn btn-link w-100 mt-2"
                  onClick={() => setCurrentStep(3)}
                  disabled={loading}
                >
                  <i className="bi bi-arrow-left me-2"></i>
                  Back
                </button>
              </>
            )}
          </div>
        );

      default:
        return null;
    }
  };

  const currentContent = stepContent[currentStep];
  const progressWidth = ((currentStep - 1) / 3) * 100;

  return (
    <div className="auth-page">
      <div className="auth-container">
        {/* Left Side - Form */}
        <div className="auth-form-section">
          <div className="auth-form-wrapper">
            {/* Logo */}
            <Link to="/" className="auth-logo">
              <div className="logo-icon">
                <span className="logo-text">C</span>
              </div>
              <span className="brand-name">CloudAyn</span>
            </Link>

            {/* Progress Stepper */}
            <div className="progress-stepper">
              <div className="progress-line" style={{ width: `${progressWidth}%` }}></div>

              <div className={`step ${currentStep >= 1 ? 'active' : ''} ${currentStep > 1 ? 'completed' : ''}`}>
                <div className="step-circle">
                  {currentStep > 1 ? <i className="bi bi-check"></i> : '1'}
                </div>
                <span className="step-label">Email</span>
              </div>

              <div className={`step ${currentStep >= 2 ? 'active' : ''} ${currentStep > 2 ? 'completed' : ''}`}>
                <div className="step-circle">
                  {currentStep > 2 ? <i className="bi bi-check"></i> : '2'}
                </div>
                <span className="step-label">Details</span>
              </div>

              <div className={`step ${currentStep >= 3 ? 'active' : ''} ${currentStep > 3 ? 'completed' : ''}`}>
                <div className="step-circle">
                  {currentStep > 3 ? <i className="bi bi-check"></i> : '3'}
                </div>
                <span className="step-label">Organization</span>
              </div>

              <div className={`step ${currentStep >= 4 ? 'active' : ''}`}>
                <div className="step-circle">4</div>
                <span className="step-label">Plan</span>
              </div>
            </div>

            {/* Form Header */}
            <div className="auth-header">
              <h1>Create Your Account</h1>
              <p>Step {currentStep} of 4: {currentContent.title}</p>
            </div>

            {/* Step Content */}
            {renderStepContent()}

            {/* Divider */}
            <div className="auth-divider">
              <span>or</span>
            </div>

            {/* Login Link */}
            <div className="auth-footer">
              <p>Already have an account?</p>
              <Link to="/login" className="auth-link">
                Sign in instead
                <i className="bi bi-arrow-right ms-2"></i>
              </Link>
            </div>

            <div className="made-in-bh">
              Made with <span className="bh-heart">❤️</span> in Bahrain 🇧🇭
            </div>
          </div>
        </div>

        {/* Right Side - Visual Explanation */}
        <div className="auth-visual-section">
          <div className="auth-visual-content">
            <div className="visual-shapes">
              <div className="visual-shape shape-1"></div>
              <div className="visual-shape shape-2"></div>
              <div className="visual-shape shape-3"></div>
            </div>

            <div className="visual-text">
              <h2>{currentContent.title}</h2>
              <p>{currentContent.description}</p>

              <div className="feature-list">
                {currentContent.features.map((feature, index) => (
                  <div key={index} className="feature-item">
                    <i className="bi bi-check-circle-fill"></i>
                    <span>{feature}</span>
                  </div>
                ))}
              </div>
            </div>

            <div className="visual-mockup">
              <div className="mockup-browser">
                <div className="browser-header">
                  <div className="browser-dots">
                    <span></span>
                    <span></span>
                    <span></span>
                  </div>
                </div>
                <div className="browser-content">
                  <div className="stat-card animate-slide-in">
                    <div className="stat-icon">🚀</div>
                    <div className="stat-info">
                      <div className="stat-value">2 min</div>
                      <div className="stat-label">Setup Time</div>
                    </div>
                  </div>
                  <div className="stat-card animate-slide-in" style={{ animationDelay: '0.2s' }}>
                    <div className="stat-icon">🔐</div>
                    <div className="stat-info">
                      <div className="stat-value">100%</div>
                      <div className="stat-label">Secure</div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ModernSignupPage;
